import { useMemo, useState } from "react";
import {
  FieldPath,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { get } from "lodash";
import { ShipmentStopType } from "@api/graphql/generated/generated-types";
import {
  StopMoveDirection,
  StopOrderRowActions,
  getStopOrderRowActions,
  getSwapIndex,
  makeNewDropOff,
  toStopOrderItems,
} from "../utils/stopOrderRowRules";

interface FormStop {
  id?: string;
  type: ShipmentStopType;
}

// Fields a stop's inputs subscribe to by exact path. `swap` does not notify them, so they are
// written again by path; a missing value gets `emptyValue`, since `undefined` keeps the old text.
const INPUT_BOUND_FIELDS: Array<{ path: string; emptyValue: unknown }> = [
  { path: "type", emptyValue: undefined },
  { path: "streetAddress", emptyValue: "" },
  { path: "companyName", emptyValue: "" },
  { path: "contactName", emptyValue: "" },
  { path: "phone", emptyValue: "" },
  { path: "suite", emptyValue: "" },
  { path: "instruction", emptyValue: "" },
  { path: "additionalDriverQuestionListId", emptyValue: null },
  { path: "stopDate", emptyValue: null },
  { path: "deliverByDate", emptyValue: null },
  { path: "pickupByDate", emptyValue: null },
  { path: "timeWindow.open", emptyValue: null },
  { path: "timeWindow.close", emptyValue: null },
];

interface UseStopOrderControlsProps<TFieldValues extends FieldValues> {
  isEnabled: boolean;
  watchStops: FormStop[] | undefined;
  completedStopIds: Set<string>;
  swap: (indexA: number, indexB: number) => void;
  insert: (index: number, value: ReturnType<typeof makeNewDropOff>) => void;
  setValue: UseFormSetValue<TFieldValues>;
  getValues: UseFormGetValues<TFieldValues>;
  fieldArrayName: string;
  fieldArrayIndexNotation?: "bracket" | "dot";
}

export interface StopOrderControls {
  isEnabled: boolean;
  getActions: (index: number) => StopOrderRowActions;
  moveStopUp: (index: number) => void;
  moveStopDown: (index: number) => void;
  insertStopBelow: (index: number) => void;
  // A form that keys its rows by index uses this key, so the rows from an insert on
  // mount again instead of showing the old stop, and the rows above it stay mounted.
  getRowKey: (index: number) => string;
}

function getValueAtPath(stop: FieldValues | undefined, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (value, key) =>
        value === null || value === undefined
          ? undefined
          : (value as FieldValues)[key],
      stop,
    );
}

/**
 * Move up / Move down / Insert stop below for a stop field array. Stops keep
 * their identity when they move, and completed stops stay in place.
 */
export function useStopOrderControls<TFieldValues extends FieldValues>({
  isEnabled,
  watchStops,
  completedStopIds,
  swap,
  insert,
  setValue,
  getValues,
  fieldArrayName,
  fieldArrayIndexNotation = "dot",
}: UseStopOrderControlsProps<TFieldValues>): StopOrderControls {
  const [rowEpochs, setRowEpochs] = useState<number[]>([]);
  const stopOrderItems = useMemo(
    () => toStopOrderItems(watchStops ?? [], completedStopIds),
    [watchStops, completedStopIds],
  );

  const getActions = (index: number): StopOrderRowActions =>
    getStopOrderRowActions(stopOrderItems, index);

  function makeFieldName(index: number, path: string): string {
    if (fieldArrayIndexNotation === "bracket") {
      return `${fieldArrayName}[${index}].${path}`;
    }
    return `${fieldArrayName}.${index}.${path}`;
  }

  function getStops(): FieldValues[] {
    return (get(getValues(), fieldArrayName) ?? []) as FieldValues[];
  }

  const rewriteInputBoundFields = (
    index: number,
    stopBeforeMove: FieldValues | undefined,
    stopAfterMove: FieldValues | undefined,
  ) => {
    INPUT_BOUND_FIELDS.forEach(({ path, emptyValue }) => {
      const nextValue = getValueAtPath(stopAfterMove, path);
      const previousValue = getValueAtPath(stopBeforeMove, path);
      if (nextValue !== undefined) {
        setValue(makeFieldName(index, path) as FieldPath<TFieldValues>, nextValue as never);
      } else if (previousValue !== undefined) {
        setValue(makeFieldName(index, path) as FieldPath<TFieldValues>, emptyValue as never);
      }
    });
  };

  // The rows from an insert on mount again, and an input with no value falls back to the form's
  // original default at that index, so missing values are written explicitly.
  const fillMissingInputValues = (fromIndex: number) => {
    const stops = getStops();
    stops.slice(fromIndex).forEach((stop, offset) => {
      INPUT_BOUND_FIELDS.forEach(({ path, emptyValue }) => {
        if (emptyValue === undefined) return;
        if (getValueAtPath(stop, path) !== undefined) return;
        setValue(
          makeFieldName(fromIndex + offset, path) as FieldPath<TFieldValues>,
          emptyValue as never,
        );
      });
    });
  };

  const insertStop = (index: number) => {
    insert(index, makeNewDropOff());
    fillMissingInputValues(index);
    const rowCount: number = getStops().length;
    setRowEpochs((epochs) =>
      Array.from(
        { length: rowCount },
        (_, row) => (epochs[row] ?? 0) + (row >= index ? 1 : 0),
      ),
    );
  };

  const moveStop = (index: number, direction: StopMoveDirection) => {
    const targetIndex: number | null = getSwapIndex(stopOrderItems, index, direction);
    if (targetIndex === null) return;
    const stopsBeforeMove: FieldValues[] = [...getStops()];
    swap(index, targetIndex);
    const stopsAfterMove: FieldValues[] = getStops();
    [index, targetIndex].forEach((rowIndex) =>
      rewriteInputBoundFields(
        rowIndex,
        stopsBeforeMove[rowIndex],
        stopsAfterMove[rowIndex],
      ),
    );
  };

  return {
    isEnabled,
    getActions,
    moveStopUp: (index) => moveStop(index, "up"),
    moveStopDown: (index) => moveStop(index, "down"),
    insertStopBelow: (index) => insertStop(index + 1),
    getRowKey: (index) => `${index}-${rowEpochs[index] ?? 0}`,
  };
}
